import { useState, useEffect, useCallback } from 'react';
import {
  ROLES_BY_STATION_TYPE, MALE_ONLY_ITEMS, FEMALE_ONLY_ITEMS,
  STRUCTURAL_SIZING, HELMET_ITEMS, ITEM_SIZE_ORDER,
  SUPABASE_URL, SUPABASE_ANON_KEY,
} from './config';

const FENZ_RED = '#C8102E';
const STEPS = ['PIN', 'Details', 'Role', 'Order', 'Review', 'Done'];

// ── Size sort utility ─────────────────────────────────────────────────────────
const SIZE_ORDER = [
  '4XS', '3XS', '2XS', 'XXS',
  'XS', 'S', 'M', 'L',
  'XL', '2XL', 'XXL',
  '3XL', '4XL', '5XL', '6XL',
];
// Compound sizes like "XS–82" — extract the leading size label
function extractSizeLabel(s) {
  const match = s.match(/^([A-Z0-9]+)/);
  return match ? match[1] : s;
}
function sortSizes(sizes) {
  if (!sizes || sizes.length === 0) return sizes;
  // Separate MISC / OSFA / one-size values to always go last
  const LAST = new Set(['MISC', 'OSFA', 'One size', 'one size']);
  const last = sizes.filter(s => LAST.has(s));
  const rest = sizes.filter(s => !LAST.has(s));

  const sorted = [...rest].sort((a, b) => {
    // Compound sizes like "XS–82" or "S/M (57cm)" — extract leading label
    const aLabel = extractSizeLabel(a);
    const bLabel = extractSizeLabel(b);
    const ai = SIZE_ORDER.indexOf(aLabel);
    const bi = SIZE_ORDER.indexOf(bLabel);
    // Both in known list
    if (ai !== -1 && bi !== -1) return ai - bi;
    // One known, one not
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    // Both purely numeric (boot sizes, waist sizes, dress sizes)
    const an = parseFloat(a);
    const bn = parseFloat(b);
    if (!isNaN(an) && !isNaN(bn)) return an - bn;
    // Fallback alphabetical
    return a.localeCompare(b);
  });

  return [...sorted, ...last];
}

// ── Form config (loaded from Edge Function) ───────────────────────────────────
// Fallback empty config while loading
const EMPTY_CONFIG = { stations: {}, roleMatrix: {}, displayNames: {}, categoryOrder: {} };

async function loadFormConfig() {
  const res = await fetch(
    `${SUPABASE_URL}/functions/v1/get-public-form-config`,
    { headers: { apikey: SUPABASE_ANON_KEY } }
  );
  if (!res.ok) throw new Error('Failed to load form config');
  return res.json();
}
async function supabaseFetch(path, options = {}) {
  const res = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    ...options,
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      'Content-Type': 'application/json',
      Prefer: 'return=representation',
      ...options.headers,
    },
  });
  if (!res.ok) {
    const err = await res.text();
    throw new Error(err);
  }
  return res.json();
}

// ── Size input component ──────────────────────────────────────────────────────
function SizeInput({ item, value, onChange }) {
  const { sizing_type, sizes, lengths, sleeves, weights } = item;
  const customSizes = ITEM_SIZE_ORDER[item.name] || null;
  const effectiveSizes = customSizes || sortSizes(sizes);

  const STRUCTURAL_ITEMS = new Set(['Structural Jacket', 'Structural Over Trousers']);

  const sel = (field, opts, placeholder) => (
    <select
      value={value?.[field] || ''}
      onChange={e => onChange({ ...value, [field]: e.target.value })}
      style={styles.select}
      required
    >
      <option value="">{placeholder}</option>
      {(opts || []).map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );

  if (STRUCTURAL_ITEMS.has(item.name)) {
    return (
      <div style={styles.sizeRow}>
        {sel('length', STRUCTURAL_SIZING.lengths, 'Select length')}
        {sel('size', sortSizes(STRUCTURAL_SIZING.sizes), 'Select size')}
      </div>
    );
  }

  if (sizing_type === 'single') {
    if (!effectiveSizes || effectiveSizes.length === 0) return null;
    if (effectiveSizes.length === 1 && ['OSFA', 'One size', 'one size'].includes(effectiveSizes[0])) {
      return <p style={styles.osfa}>One size fits all</p>;
    }
    return <div style={styles.sizeRow}>{sel('size', effectiveSizes, 'Select size')}</div>;
  }

  if (sizing_type === 'length+size') {
    return (
      <div style={styles.sizeRow}>
        {sel('length', lengths, 'Select length')}
        {sel('size', sizes, 'Select size')}
      </div>
    );
  }

  if (sizing_type === 'sleeve+size') {
    return (
      <div style={styles.sizeRow}>
        {sel('sleeve', sleeves, 'Select sleeve')}
        {sel('size', sizes, 'Select size')}
      </div>
    );
  }

  if (sizing_type === 'weight+size') {
    return (
      <div style={styles.sizeRow}>
        {sel('weight', weights || ['Heavyweight', 'Lightweight'], 'Select weight')}
        {sel('size', sizes, 'Select size')}
      </div>
    );
  }

  return null;
}

// ── Helmet item (merged Yellow/Red variant) ───────────────────────────────────
function HelmetItem({ items, selections, onToggle, onSize, onVariant, hasError }) {
  const helmetKey = 'STRUCTURAL_HELMET';
  const selected = selections[helmetKey];
  const variant = selected?.variant || '';

  const targetItem = variant === 'Yellow'
    ? items.find(i => i.name === 'Structural Helmet – Firefighter (Yellow)')
    : variant === 'Red'
      ? items.find(i => i.name === 'Structural Helmet – Officer (Red)')
      : null;

  return (
    <div
      id={`item-${helmetKey}`}
      style={{
        ...styles.itemCard,
        ...(selected ? styles.itemCardSelected : {}),
        ...(hasError ? styles.itemCardError : {}),
      }}
    >
      <div style={styles.itemHeader}>
        <label style={styles.itemLabel}>
          <input
            type="checkbox"
            checked={!!selected}
            onChange={() => onToggle(helmetKey, items[0])}
            style={styles.checkbox}
          />
          <span>Structural Helmet</span>
        </label>
      </div>
      {selected && (
        <div style={styles.itemControls}>
          <select
            value={variant}
            onChange={e => onVariant(helmetKey, e.target.value)}
            style={styles.select}
          >
            <option value="">Select colour</option>
            <option value="Yellow">Firefighter – Yellow</option>
            <option value="Red">Officer – Red</option>
          </select>
          {hasError && !variant && <p style={styles.sizeError}>Please select a colour</p>}
          {targetItem && <p style={styles.osfa}>One size fits all</p>}
        </div>
      )}
    </div>
  );
}

// ── Item card ─────────────────────────────────────────────────────────────────
function ItemCard({ item, selected, onToggle, sizeValue, onSize, hasError }) {
  const displayName = item._displayName || item.name;
  const isSingleOsfa = item.sizing_type === 'single' &&
    item.sizes?.length === 1 &&
    ['OSFA', 'One size', 'one size'].includes(item.sizes[0]);

  return (
    <div
      id={`item-${item.id}`}
      style={{
        ...styles.itemCard,
        ...(selected ? styles.itemCardSelected : {}),
        ...(hasError ? styles.itemCardError : {}),
      }}
    >
      <div style={styles.itemHeader}>
        <label style={styles.itemLabel}>
          <input
            type="checkbox"
            checked={!!selected}
            onChange={() => onToggle(item.id, item)}
            style={styles.checkbox}
          />
          <span>{displayName}</span>
        </label>
      </div>
      {selected && !isSingleOsfa && (
        <div style={styles.itemControls}>
          <SizeInput item={item} value={sizeValue} onChange={onSize} />
          {hasError && <p style={styles.sizeError}>Please select a size</p>}
        </div>
      )}
      {selected && isSingleOsfa && (
        <p style={styles.osfa}>One size fits all</p>
      )}
    </div>
  );
}

// ── Step indicator ────────────────────────────────────────────────────────────
function StepBar({ current }) {
  const labels = ['PIN', 'Details', 'Role', 'Order', 'Review'];
  return (
    <div style={styles.stepBar}>
      {labels.map((label, i) => (
        <div key={label} style={styles.stepItem}>
          <div style={{
            ...styles.stepDot,
            background: i < current ? FENZ_RED : i === current ? FENZ_RED : '#ddd',
            opacity: i <= current ? 1 : 0.4,
          }}>
            {i < current ? '✓' : i + 1}
          </div>
          <span style={{ ...styles.stepLabel, opacity: i <= current ? 1 : 0.4 }}>{label}</span>
        </div>
      ))}
    </div>
  );
}

// ── Main App ──────────────────────────────────────────────────────────────────
export default function App() {
  const [step, setStep] = useState(0);

  // ── Load form config from Edge Function ──────────────────────────────────────
  const [formConfig, setFormConfig] = useState(EMPTY_CONFIG);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState('');

  useEffect(() => {
    loadFormConfig()
      .then(cfg => { setFormConfig(cfg); setConfigLoading(false); })
      .catch(() => { setConfigError('Could not load form configuration. Please refresh and try again.'); setConfigLoading(false); });
  }, []);

  const { stations: STATIONS, roleMatrix: ROLE_MATRIX, displayNames: DISPLAY_NAMES, categoryOrder: CATEGORY_ORDER_DATA } = formConfig;
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [station, setStation] = useState(null);

  const [details, setDetails] = useState({
    ordererName: '', ordererPhone: '', ordererEmail: '',
    recipientName: '', recipientEmployeeId: '', recipientPhone: '',
  });
  const [detailsError, setDetailsError] = useState({});
  const [additionalNotes, setAdditionalNotes] = useState('');

  const [gender, setGender] = useState('');
  const [role, setRole] = useState('');

  const [catalogueItems, setCatalogueItems] = useState([]);
  const [catalogueLoading, setCatalogueLoading] = useState(false);
  const [catalogueError, setCatalogueError] = useState('');
  const [selections, setSelections] = useState({});
  const [sizeValues, setSizeValues] = useState({});

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  // Load catalogue items from Supabase
  const loadCatalogue = useCallback(async () => {
    setCatalogueLoading(true);
    setCatalogueError('');
    try {
      const items = await supabaseFetch(
        'uniform_items?active=eq.true&select=id,name,category,sizing_type'
      );
      console.log('Catalogue items loaded:', items.length);
      // For each item, load sizes from inventory
      const withSizes = await Promise.all(items.map(async item => {
        const inv = await supabaseFetch(
          `uniform_inventory?item_id=eq.${item.id}&select=size,length,sleeve,weight`
        );
        const sizes = sortSizes([...new Set(inv.map(r => r.size).filter(Boolean))]);
        const lengths = [...new Set(inv.map(r => r.length).filter(Boolean))].sort();
        const sleeves = [...new Set(inv.map(r => r.sleeve).filter(Boolean))].sort();
        const weights = [...new Set(inv.map(r => r.weight).filter(Boolean))].sort();
        return { ...item, sizes, lengths, sleeves, weights };
      }));
      console.log('Role:', role, '| Gender:', gender, '| Items with sizes:', withSizes.length);
      setCatalogueItems(withSizes);
    } catch (e) {
      console.error('Failed to load catalogue', e);
      setCatalogueError('Could not load catalogue. Please check your connection and try again.');
    } finally {
      setCatalogueLoading(false);
    }
  }, [role, gender]);

  useEffect(() => {
    if (step === 3 && catalogueItems.length === 0) {
      loadCatalogue();
    }
  }, [step, catalogueItems.length, loadCatalogue]);

  // Filter items by role and gender
  // Build catalogue from Edge Function config — items already ordered and grouped
  // CATEGORY_ORDER_DATA: { [displayCat]: { items: [...], order: N } }
  const visibleItems = catalogueItems.filter(item => {
    if (HELMET_ITEMS.has(item.name)) return false;
    const allowedRoles = ROLE_MATRIX[item.name];
    if (!allowedRoles || !allowedRoles.includes(role)) return false;
    if (gender === 'male' && FEMALE_ONLY_ITEMS.has(item.name)) return false;
    if (gender === 'female' && MALE_ONLY_ITEMS.has(item.name)) return false;
    return true;
  });

  // Build display categories from Edge Function item order
  // Each category has items pre-sorted by display_order from DB
  const sortedCategories = Object.entries(CATEGORY_ORDER_DATA || {})
    .sort((a, b) => (a[1].order ?? 99) - (b[1].order ?? 99))
    .map(([cat]) => cat);

  // For each display category, get visible items in DB order
  const grouped = {};
  for (const [cat, catData] of Object.entries(CATEGORY_ORDER_DATA || {})) {
    const catItems = (catData.items || [])
      .filter(cfgItem => visibleItems.find(i => i.id === cfgItem.id))
      .map(cfgItem => {
        const catalogueItem = visibleItems.find(i => i.id === cfgItem.id);
        return catalogueItem ? { ...catalogueItem, _displayName: cfgItem.display_name } : null;
      })
      .filter(Boolean);
    if (catItems.length > 0) grouped[cat] = catItems;
  }

  // Should structural helmets show?
  const showHelmet = ['structural', 'rural'].includes(role) &&
    catalogueItems.some(i => i.name === 'Structural Helmet – Firefighter (Yellow)');

  const helmetItems = catalogueItems.filter(i => HELMET_ITEMS.has(i.name));

  // Handlers
  function handlePinSubmit() {
    const num = parseInt(pin, 10);
    const found = STATIONS[num];
    if (!found) {
      setPinError('Station not found. Please check your Business Unit number and try again.');
      return;
    }
    setPinError('');
    setStation({ ...found, bu: num });
    setStep(1);
  }

  function handleDetailsNext() {
    const errors = {};
    if (!details.ordererName.trim()) errors.ordererName = 'Your name is required';
    if (!details.ordererPhone.trim()) errors.ordererPhone = 'Your phone number is required';
    if (!details.ordererEmail.trim()) {
      errors.ordererEmail = 'Your email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(details.ordererEmail.trim())) {
      errors.ordererEmail = 'Please enter a valid email address';
    }
    if (!details.recipientName.trim()) errors.recipientName = 'Recipient name is required';
    if (!details.recipientEmployeeId.trim()) {
      errors.recipientEmployeeId = 'Employee ID is required';
    } else if (!/^\d+$/.test(details.recipientEmployeeId.trim())) {
      errors.recipientEmployeeId = 'Employee ID must contain numbers only';
    }
    if (!details.recipientPhone.trim()) errors.recipientPhone = 'Recipient phone number is required';
    if (Object.keys(errors).length > 0) {
      setDetailsError(errors);
      return;
    }
    setDetailsError({});
    setStep(2);
  }

  function handleRoleNext() {
    if (!gender || !role) return;
    setSelections({});
    setSizeValues({});
    setStep(3);
  }

  function toggleItem(id, item) {
    setSelections(prev => {
      const next = { ...prev };
      if (next[id]) {
        delete next[id];
        setSizeValues(sv => { const s = { ...sv }; delete s[id]; return s; });
      } else {
        next[id] = item;
      }
      return next;
    });
  }

  function handleVariant(key, variant) {
    setSelections(prev => ({ ...prev, [key]: { ...prev[key], variant } }));
    setSizeErrors(prev => { const e = { ...prev }; delete e[key]; return e; });
  }

  function handleSizeChange(id, val) {
    setSizeValues(prev => ({ ...prev, [id]: val }));
    setSizeErrors(prev => { const e = { ...prev }; delete e[id]; return e; });
  }

  const [sizeErrors, setSizeErrors] = useState({});

  function isSizeComplete(key, item) {
    // Helmet — needs variant selected
    if (key === 'STRUCTURAL_HELMET') return !!item?.variant;
    const sz = sizeValues[key] || {};
    // OSFA / One size items — always complete
    const OSFA = new Set(['OSFA', 'One size', 'one size']);
    if (item?.sizes?.length === 1 && OSFA.has(item.sizes[0])) return true;
    if (item?.sizes?.length === 0) return true;
    // Structural jacket/trouser — need length + size
    const STRUCTURAL_ITEMS = new Set(['Structural Jacket', 'Structural Over Trousers']);
    if (STRUCTURAL_ITEMS.has(item?.name)) return !!sz.length && !!sz.size;
    // By sizing_type
    switch (item?.sizing_type) {
      case 'single': return !!sz.size;
      case 'length+size': return !!sz.length && !!sz.size;
      case 'sleeve+size': return !!sz.sleeve && !!sz.size;
      case 'weight+size': return !!sz.weight && !!sz.size;
      default: return true;
    }
  }

  function handleReview() {
    if (Object.keys(selections).length === 0) {
      alert('Please select at least one item before continuing.');
      return;
    }
    // Validate all selected items have complete sizing
    const errors = {};
    for (const [key, item] of Object.entries(selections)) {
      if (!isSizeComplete(key, item)) {
        errors[key] = true;
      }
    }
    if (Object.keys(errors).length > 0) {
      setSizeErrors(errors);
      // Scroll to first error
      const firstKey = Object.keys(errors)[0];
      document.getElementById(`item-${firstKey}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setSizeErrors({});
    setStep(4);
  }

  async function handleSubmit() {
    setSubmitting(true);
    setSubmitError('');
    try {
      // Build order payload
      const order = {
        recipient_name: details.recipientName.trim(),
        recipient_employee_id: details.recipientEmployeeId.trim(),
        station: station.name,
        phone: details.recipientPhone.trim(),
        role,
        orderer_email: details.ordererEmail.trim(),
        additional_notes: additionalNotes.trim() || null,
        notes: `Role: ${role} | Gender: ${gender} | BU: ${station.bu} | Ordered by: ${details.ordererName.trim()} (${details.ordererPhone.trim()}, ${details.ordererEmail.trim()})`,
      };

      // Build items array + itemNames map for Level 2 draft detection
      const items = [];
      const itemNames = {};
      for (const [key, item] of Object.entries(selections)) {
        if (key === 'STRUCTURAL_HELMET') {
          const helmetName = item.variant === 'Yellow'
            ? 'Structural Helmet – Firefighter (Yellow)'
            : 'Structural Helmet – Officer (Red)';
          const helmetItem = catalogueItems.find(i => i.name === helmetName);
          if (helmetItem) {
            items.push({ item_id: helmetItem.id, size: 'One size' });
            itemNames[helmetItem.id] = { name: helmetItem.name, size: 'One size' };
          }
        } else {
          const sz = sizeValues[key] || {};
          items.push({
            item_id: key,
            size: sz.size || null,
            length: sz.length || null,
            sleeve: sz.sleeve || null,
            weight: sz.weight || null,
          });
          itemNames[key] = {
            name: item.name,
            size: sz.size || null,
            length: sz.length || null,
            sleeve: sz.sleeve || null,
            weight: sz.weight || null,
          };
        }
      }

      // Call Edge Function
      const res = await fetch(
        `${SUPABASE_URL}/functions/v1/receive-public-uniform-order`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': SUPABASE_ANON_KEY,
          },
          body: JSON.stringify({ order, items, itemNames }),
        }
      );

      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'Submission failed');

      setStep(5);
    } catch (e) {
      console.error(e);
      setSubmitError('Something went wrong submitting your order. Please try again or contact your VSO.');
    } finally {
      setSubmitting(false);
    }
  }

  // ── Render steps ─────────────────────────────────────────────────────────────
  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <div style={styles.headerInner}>
          <div style={styles.logo}>
            <div>
              <div style={styles.logoTitle}>Waitemata District</div>
              <div style={styles.logoSub}>Uniform Order</div>
            </div>
          </div>
        </div>
      </header>

      <main style={styles.main}>
        {step < 5 && <StepBar current={step} />}

        {/* ── Config loading/error ── */}
        {configLoading && (
          <div style={styles.loading}>Loading form configuration…</div>
        )}
        {configError && (
          <div style={{ ...styles.card, color: FENZ_RED }}>{configError}</div>
        )}

        {/* ── STEP 0: PIN ── */}
        {!configLoading && !configError && step === 0 && (
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Waitemata District Volunteer Uniform Order Form</h2>
            <p style={styles.cardDesc}>
              Enter your station PIN number to get started. Contact your VSO if you don't know it.
            </p>
            <div style={styles.pinWrap}>
              <input
                type="number"
                placeholder=""
                value={pin}
                onChange={e => setPin(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handlePinSubmit()}
                style={styles.pinInput}
                autoFocus
              />
            </div>
            {pinError && <p style={styles.error}>{pinError}</p>}
            <button onClick={handlePinSubmit} style={{ ...styles.btnPrimary, marginTop: 20 }}>
              Continue
            </button>
          </div>
        )}

        {/* ── STEP 1: Details ── */}
        {!configLoading && !configError && step === 1 && (
          <div style={styles.card}>
            <div style={styles.stationBadge}>{station?.name}</div>
            <h2 style={styles.cardTitle}>Order details</h2>

            <div style={styles.sectionDivider}>
              <span style={styles.sectionLabel}>Person placing this order</span>
            </div>
            <p style={{ ...styles.cardDesc, marginBottom: 12 }}>
              This may be a uniform officer or VSO ordering on behalf of someone else.
            </p>
            <div style={styles.formGroup}>
              <label style={styles.label}>Your name *</label>
              <input
                type="text"
                placeholder="First and last name"
                value={details.ordererName}
                onChange={e => setDetails(d => ({ ...d, ordererName: e.target.value }))}
                style={styles.input}
              />
              {detailsError.ordererName && <p style={styles.error}>{detailsError.ordererName}</p>}
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Your phone number *</label>
              <input
                type="tel"
                placeholder="Mobile number"
                value={details.ordererPhone}
                onChange={e => setDetails(d => ({ ...d, ordererPhone: e.target.value }))}
                style={styles.input}
              />
              {detailsError.ordererPhone && <p style={styles.error}>{detailsError.ordererPhone}</p>}
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Your email address *</label>
              <input
                type="email"
                placeholder="name@fireandemergency.nz"
                value={details.ordererEmail}
                onChange={e => setDetails(d => ({ ...d, ordererEmail: e.target.value }))}
                style={styles.input}
                autoCapitalize="none"
              />
              {detailsError.ordererEmail && <p style={styles.error}>{detailsError.ordererEmail}</p>}
            </div>

            <div style={styles.sectionDivider}>
              <span style={styles.sectionLabel}>Uniform is for</span>
            </div>
            <p style={{ ...styles.cardDesc, marginBottom: 12 }}>
              Details of the person who will be receiving this uniform.
            </p>
            <div style={styles.formGroup}>
              <label style={styles.label}>Recipient full name *</label>
              <input
                type="text"
                placeholder="First and last name"
                value={details.recipientName}
                onChange={e => setDetails(d => ({ ...d, recipientName: e.target.value }))}
                style={styles.input}
              />
              {detailsError.recipientName && <p style={styles.error}>{detailsError.recipientName}</p>}
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Recipient employee ID *</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                placeholder="FENZ employee number"
                value={details.recipientEmployeeId}
                onChange={e => setDetails(d => ({ ...d, recipientEmployeeId: e.target.value.replace(/\D/g, '') }))}
                style={styles.input}
              />
              {detailsError.recipientEmployeeId && <p style={styles.error}>{detailsError.recipientEmployeeId}</p>}
            </div>
            <div style={styles.formGroup}>
              <label style={styles.label}>Recipient phone number *</label>
              <input
                type="tel"
                placeholder="Mobile number"
                value={details.recipientPhone}
                onChange={e => setDetails(d => ({ ...d, recipientPhone: e.target.value }))}
                style={styles.input}
              />
              {detailsError.recipientPhone && <p style={styles.error}>{detailsError.recipientPhone}</p>}
            </div>

            <div style={styles.btnRow}>
              <button onClick={() => setStep(0)} style={styles.btnSecondary}>Back</button>
              <button onClick={handleDetailsNext} style={styles.btnPrimary}>Continue</button>
            </div>
          </div>
        )}

        {/* ── STEP 2: Gender + Role ── */}
        {!configLoading && !configError && step === 2 && (
          <div style={styles.card}>
            <div style={styles.stationBadge}>{station?.name}</div>
            <h2 style={styles.cardTitle}>Your role</h2>

            <div style={styles.formGroup}>
              <label style={styles.label}>Gender *</label>
              <div style={styles.optionGroup}>
                {['male', 'female'].map(g => (
                  <button
                    key={g}
                    onClick={() => setGender(g)}
                    style={{ ...styles.optionBtn, ...(gender === g ? styles.optionBtnActive : {}) }}
                  >
                    {g.charAt(0).toUpperCase() + g.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Role *</label>
              <div style={styles.optionGroup}>
                {(ROLES_BY_STATION_TYPE[station?.type] || []).map(r => (
                  <button
                    key={r.value}
                    onClick={() => setRole(r.value)}
                    style={{ ...styles.optionBtn, ...(role === r.value ? styles.optionBtnActive : {}) }}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={styles.btnRow}>
              <button onClick={() => setStep(1)} style={styles.btnSecondary}>Back</button>
              <button
                onClick={handleRoleNext}
                style={{ ...styles.btnPrimary, opacity: (!gender || !role) ? 0.5 : 1 }}
                disabled={!gender || !role}
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 3: Catalogue ── */}
        {!configLoading && !configError && step === 3 && (
          <div>
            <div style={styles.catalogueHeader}>
              <div style={styles.stationBadge}>{station?.name}</div>
              <h2 style={styles.cardTitle}>Select items</h2>
              <p style={styles.cardDesc}>
                Tick each item you need and select the correct size. Items shown are based on your role and station.
              </p>
            </div>

            {catalogueLoading ? (
              <div style={styles.loading}>Loading catalogue…</div>
            ) : catalogueError ? (
              <div style={{ ...styles.card, color: FENZ_RED }}>{catalogueError}</div>
            ) : (
              <>
                {sortedCategories.filter(cat => grouped[cat]?.length > 0 || (cat === 'Structural PPE' && showHelmet)).map(cat => (
                  <div key={cat} style={styles.categorySection}>
                    <h3 style={styles.categoryTitle}>{cat}</h3>
                    <div style={styles.itemGrid}>
                      {cat === 'Structural PPE' && showHelmet && (
                        <HelmetItem
                          items={helmetItems}
                          selections={selections}
                          onToggle={toggleItem}
                          onSize={handleSizeChange}
                          onVariant={handleVariant}
                          hasError={!!sizeErrors['STRUCTURAL_HELMET']}
                        />
                      )}
                      {(grouped[cat] || []).map(item => (
                        <ItemCard
                          key={item.id}
                          item={item}
                          selected={!!selections[item.id]}
                          onToggle={toggleItem}
                          sizeValue={sizeValues[item.id]}
                          onSize={val => handleSizeChange(item.id, val)}
                          hasError={!!sizeErrors[item.id]}
                        />
                      ))}
                    </div>
                  </div>
                ))}

                <div style={styles.stickyBottom}>
                  <div style={styles.btnRow}>
                    <button onClick={() => setStep(2)} style={styles.btnSecondary}>Back</button>
                    <button onClick={handleReview} style={styles.btnPrimary}>
                      Review order ({Object.keys(selections).length} item{Object.keys(selections).length !== 1 ? 's' : ''})
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── STEP 4: Review ── */}
        {!configLoading && !configError && step === 4 && (
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Review your order</h2>

            <div style={styles.reviewSection}>
              <h3 style={styles.reviewHeading}>Ordered by</h3>
              <div style={styles.reviewRow}><span>Name</span><strong>{details.ordererName}</strong></div>
              <div style={styles.reviewRow}><span>Phone</span><strong>{details.ordererPhone}</strong></div>
              <div style={styles.reviewRow}><span>Email</span><strong>{details.ordererEmail}</strong></div>
            </div>

            <div style={styles.reviewSection}>
              <h3 style={styles.reviewHeading}>Uniform for</h3>
              <div style={styles.reviewRow}><span>Name</span><strong>{details.recipientName}</strong></div>
              <div style={styles.reviewRow}><span>Employee ID</span><strong>{details.recipientEmployeeId}</strong></div>
              <div style={styles.reviewRow}><span>Phone</span><strong>{details.recipientPhone}</strong></div>
              <div style={styles.reviewRow}><span>Station</span><strong>{station?.name}</strong></div>
              <div style={styles.reviewRow}>
                <span>Role</span>
                <strong>{ROLES_BY_STATION_TYPE[station?.type]?.find(r => r.value === role)?.label}</strong>
              </div>
            </div>

            <div style={styles.reviewSection}>
              <h3 style={styles.reviewHeading}>Items ordered</h3>
              {Object.entries(selections).map(([key, item]) => {
                const sz = sizeValues[key] || {};
                let displayName;
                if (key === 'STRUCTURAL_HELMET') {
                  displayName = `Structural Helmet – ${item.variant || 'Not selected'}`;
                } else {
                  displayName = item._displayName || item.name;
                }
                const sizeParts = [sz.weight, sz.sleeve, sz.length, sz.size].filter(Boolean);
                return (
                  <div key={key} style={styles.reviewItem}>
                    <span style={styles.reviewItemName}>{displayName}</span>
                    {sizeParts.length > 0 && (
                      <span style={styles.reviewItemSize}>{sizeParts.join(' / ')}</span>
                    )}
                  </div>
                );
              })}
            </div>

            <div style={styles.reviewSection}>
              <h3 style={styles.reviewHeading}>Anything else? (optional)</h3>
              <textarea
                placeholder="Add any notes for the VSO team — e.g. sizing preferences or special requests"
                value={additionalNotes}
                onChange={e => setAdditionalNotes(e.target.value)}
                rows={3}
                style={{ ...styles.input, resize: 'vertical', fontFamily: 'inherit' }}
              />
            </div>

            {submitError && <p style={styles.error}>{submitError}</p>}

            <div style={styles.btnRow}>
              <button onClick={() => setStep(3)} style={styles.btnSecondary}>Edit</button>
              <button
                onClick={handleSubmit}
                style={{ ...styles.btnPrimary, opacity: submitting ? 0.6 : 1 }}
                disabled={submitting}
              >
                {submitting ? 'Submitting…' : 'Submit order'}
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 5: Done ── */}
        {!configLoading && !configError && step === 5 && (
          <div style={styles.card}>
            <div style={styles.successIcon}>✓</div>
            <h2 style={styles.cardTitle}>Order received</h2>
            <p style={styles.cardDesc}>
              Your uniform order has been submitted to the Waitemata District VSO team. Your order will be dispatched to your station once it is ready.
            </p>
            <p style={styles.cardDesc}>
              If you have any questions, contact your station VSO directly.
            </p>
            <button
              onClick={() => {
                setStep(0);
                setPin('');
                setStation(null);
                setDetails({ ordererName: '', ordererPhone: '', ordererEmail: '', recipientName: '', recipientEmployeeId: '', recipientPhone: '' });
                setAdditionalNotes('');
                setGender('');
                setRole('');
                setSelections({});
                setSizeValues({});
                setSizeErrors({});
              }}
              style={styles.btnPrimary}
            >
              Submit another order
            </button>
          </div>
        )}
      </main>

      <footer style={styles.footer}>
        <p>Fire and Emergency New Zealand — Waitemata District</p>
        <p>Volunteer Uniform Orders</p>
      </footer>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const styles = {
  page: {
    minHeight: '100vh',
    background: '#f5f5f5',
    fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    background: FENZ_RED,
    color: '#fff',
    padding: '0',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
  },
  headerInner: {
    maxWidth: 720,
    margin: '0 auto',
    padding: '12px 16px',
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  logoTitle: {
    fontWeight: 700,
    fontSize: 16,
    lineHeight: 1.2,
  },
  logoSub: {
    fontSize: 12,
    opacity: 0.85,
    lineHeight: 1.2,
  },
  main: {
    flex: 1,
    maxWidth: 720,
    width: '100%',
    margin: '0 auto',
    padding: '16px 16px 100px',
    boxSizing: 'border-box',
  },
  stepBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 24,
    padding: '16px 8px 0',
  },
  stepItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 4,
    flex: 1,
  },
  stepDot: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 700,
    transition: 'all 0.2s',
  },
  stepLabel: {
    fontSize: 10,
    color: '#555',
    textAlign: 'center',
    transition: 'opacity 0.2s',
  },
  card: {
    background: '#fff',
    borderRadius: 12,
    padding: '24px 20px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
    marginBottom: 16,
  },
  cardTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: '#1a1a1a',
    margin: '8px 0 8px',
  },
  cardDesc: {
    fontSize: 14,
    color: '#555',
    lineHeight: 1.5,
    margin: '0 0 16px',
  },
  stationBadge: {
    display: 'inline-block',
    background: '#fff3cd',
    border: '1px solid #ffc107',
    color: '#7d5700',
    borderRadius: 6,
    padding: '3px 10px',
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 8,
  },
  formGroup: {
    marginBottom: 16,
  },
  label: {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#333',
    marginBottom: 6,
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    border: '1.5px solid #ddd',
    borderRadius: 8,
    fontSize: 15,
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'border-color 0.15s',
  },
  select: {
    flex: 1,
    padding: '8px 10px',
    border: '1.5px solid #ddd',
    borderRadius: 8,
    fontSize: 14,
    background: '#fff',
    outline: 'none',
    minWidth: 0,
  },
  error: {
    color: FENZ_RED,
    fontSize: 13,
    margin: '4px 0 0',
  },
  btnPrimary: {
    flex: 1,
    background: FENZ_RED,
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '13px 20px',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'opacity 0.15s',
  },
  btnSecondary: {
    flex: '0 0 auto',
    background: '#f0f0f0',
    color: '#333',
    border: 'none',
    borderRadius: 8,
    padding: '13px 20px',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
  },
  btnRow: {
    display: 'flex',
    gap: 10,
    marginTop: 20,
  },
  optionGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
  },
  optionBtn: {
    padding: '10px 16px',
    border: '2px solid #ddd',
    borderRadius: 8,
    background: '#fff',
    fontSize: 14,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  optionBtnActive: {
    border: `2px solid ${FENZ_RED}`,
    background: '#fff5f7',
    color: FENZ_RED,
    fontWeight: 700,
  },
  catalogueHeader: {
    background: '#fff',
    borderRadius: 12,
    padding: '20px 20px 12px',
    marginBottom: 12,
    boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
  },
  categorySection: {
    marginBottom: 20,
  },
  categoryTitle: {
    fontSize: 13,
    fontWeight: 700,
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    margin: '0 0 8px',
    padding: '0 4px',
  },
  itemGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  itemCard: {
    background: '#fff',
    border: '1.5px solid #e8e8e8',
    borderRadius: 10,
    padding: '12px 14px',
    transition: 'border-color 0.15s, box-shadow 0.15s',
  },
  itemCardError: {
    border: `1.5px solid ${FENZ_RED}`,
    boxShadow: `0 0 0 3px rgba(200,16,46,0.15)`,
    background: '#fff8f8',
  },
  sizeError: {
    color: FENZ_RED,
    fontSize: 12,
    margin: '6px 0 0',
    fontWeight: 600,
  },
  itemCardSelected: {
    border: `1.5px solid ${FENZ_RED}`,
    boxShadow: `0 0 0 3px rgba(200,16,46,0.08)`,
  },
  itemHeader: {
    display: 'flex',
    alignItems: 'center',
  },
  itemLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    cursor: 'pointer',
    fontSize: 15,
    fontWeight: 500,
    color: '#1a1a1a',
    flex: 1,
  },
  checkbox: {
    width: 18,
    height: 18,
    accentColor: FENZ_RED,
    cursor: 'pointer',
    flexShrink: 0,
  },
  itemControls: {
    marginTop: 10,
    paddingTop: 10,
    borderTop: '1px solid #f0f0f0',
  },
  sizeRow: {
    display: 'flex',
    gap: 8,
  },
  osfa: {
    margin: '4px 0 0',
    fontSize: 13,
    color: '#888',
    fontStyle: 'italic',
  },
  stickyBottom: {
    position: 'sticky',
    bottom: 0,
    background: 'linear-gradient(to top, #f5f5f5 80%, transparent)',
    padding: '16px 0 8px',
    marginTop: 8,
  },
  loading: {
    textAlign: 'center',
    padding: 40,
    color: '#888',
    fontSize: 15,
  },
  reviewSection: {
    marginBottom: 20,
  },
  reviewHeading: {
    fontSize: 13,
    fontWeight: 700,
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    margin: '0 0 10px',
  },
  reviewRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '7px 0',
    borderBottom: '1px solid #f0f0f0',
    fontSize: 14,
    color: '#444',
    gap: 12,
  },
  reviewItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: '1px solid #f0f0f0',
    gap: 12,
  },
  reviewItemName: {
    fontSize: 14,
    color: '#1a1a1a',
    flex: 1,
  },
  reviewItemSize: {
    fontSize: 13,
    color: '#666',
    background: '#f5f5f5',
    borderRadius: 5,
    padding: '2px 8px',
    flexShrink: 0,
  },
  successIcon: {
    width: 56,
    height: 56,
    borderRadius: '50%',
    background: '#e8f5e9',
    color: '#2e7d32',
    fontSize: 28,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px',
  },
  pinWrap: {
    marginTop: 16,
    marginBottom: 4,
  },
  pinInput: {
    width: 200,
    padding: '10px 12px',
    border: '1.5px solid #ddd',
    borderRadius: 8,
    fontSize: 20,
    fontWeight: 600,
    letterSpacing: '0.1em',
    outline: 'none',
    display: 'block',
  },
  sectionDivider: {
    borderTop: '2px solid #f0f0f0',
    marginBottom: 14,
    marginTop: 8,
    paddingTop: 16,
  },
  sectionLabel: {
    fontSize: 13,
    fontWeight: 700,
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  footer: {
    textAlign: 'center',
    padding: '20px 16px',
    fontSize: 12,
    color: '#aaa',
    lineHeight: 1.6,
  },
};
